import { type MouseEvent } from "react";
import {
  Body1,
  Caption1,
  Card,
  Checkbox,
  Tooltip,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from "@fluentui/react-components";
import { Attach12Regular, Comment12Regular, MoreHorizontal16Regular } from "@fluentui/react-icons";
import type { ExecutionPriority, ExecutionTask } from "@/engine/types";
import { knownAssignees, PRIORITY_LABEL } from "@/store/execution-task-ops";
import { useProjectStore } from "@/store/project-store";
import { FuiButton } from "@/components/fui-control";
import { useOwnWork, usePlanSurface } from "@/features/access/use-plan-access";
import { GateTagMicroPopover } from "./GateTagMicroPopover";
import { openScopeInSchedule } from "./links";
import { TaskCommandMenu } from "./TaskCommandMenu";
import { AssigneeAvatars, ChecklistMini, DueChip, LabelChips, PriorityBadge, ScopeChips, StatusBadge } from "./task-ui";
import { isScopeGroupKey, type GroupKey, type TaskQueryContext } from "./task-query";

const useStyles = makeStyles({
  card: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    boxSizing: "border-box",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    gap: "6px",
    cursor: "pointer",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
    ...shorthands.border("1px", "solid", tokens.colorTransparentStroke),
    transitionProperty: "box-shadow, transform, border-color",
    transitionDuration: tokens.durationFaster,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1,
      boxShadow: tokens.shadow8,
      transform: "translateY(-1px)",
      ...shorthands.borderColor(tokens.colorNeutralStroke2),
    },
  },
  selected: {
    outline: `2px solid ${tokens.colorBrandStroke1}`,
    outlineOffset: "-1px",
  },
  done: {
    opacity: 0.72,
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "2px",
    minWidth: 0,
    width: "100%",
  },
  titleWrap: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    alignItems: "center",
  },
  title: {
    minWidth: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  titleDone: {
    textDecoration: "line-through",
    color: tokens.colorNeutralForeground3,
  },
  moreBtn: {
    opacity: 0.6,
    flexShrink: 0,
    marginLeft: "auto",
    ":hover": {
      opacity: 1,
    },
  },
  notesRow: {
    width: "100%",
    minWidth: 0,
    marginTop: "-2px",
  },
  notes: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    display: "-webkit-box",
    WebkitLineClamp: 4,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    textOverflow: "ellipsis",
    wordBreak: "break-word",
    whiteSpace: "pre-line",
    width: "100%",
  },
  notesDone: {
    textDecoration: "line-through",
    opacity: 0.7,
  },
  chipsRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "6px",
    minWidth: 0,
  },
  bucketText: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    marginTop: "auto",
    paddingTop: tokens.spacingVerticalS,
    minWidth: 0,
    ...shorthands.borderTop("1px", "solid", tokens.colorNeutralStroke2),
  },
  footerLeft: {
    flex: 1,
    minWidth: 0,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
  },
  footerRight: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
    marginLeft: "auto",
  },
  metaItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: "2px",
    color: tokens.colorNeutralForeground3,
  },
  project: {
    color: tokens.colorBrandForeground2,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  coverWrap: {
    margin: `-${tokens.spacingVerticalS} -${tokens.spacingHorizontalM} ${tokens.spacingVerticalXS} -${tokens.spacingHorizontalM}`,
    overflow: "hidden",
    borderTopLeftRadius: tokens.borderRadiusMedium,
    borderTopRightRadius: tokens.borderRadiusMedium,
    height: "112px",
    backgroundColor: tokens.colorNeutralBackground3,
  },
  coverImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  onCardChecklist: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    marginTop: "2px",
    paddingTop: "4px",
    ...shorthands.borderTop("1px", "dashed", tokens.colorNeutralStroke3),
  },
  onCardCheckRow: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    minWidth: 0,
    cursor: "default",
    minHeight: "18px",
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    padding: "1px 2px",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  onCardCheckbox: {
    minWidth: "auto",
    ...shorthands.padding(0),
    ...shorthands.margin(0),
    flexShrink: 0,
    "& .fui-Checkbox__indicator": {
      width: "12px",
      height: "12px",
      minWidth: "12px",
      minHeight: "12px",
      ...shorthands.margin(0),
      fontSize: "8px",
    },
    "& .fui-Checkbox__indicator svg": {
      width: "8px",
      height: "8px",
    },
  },
  onCardCheckText: {
    flex: 1,
    minWidth: 0,
    fontSize: tokens.fontSizeBase100,
    lineHeight: tokens.lineHeightBase200,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: tokens.colorNeutralForeground2,
  },
  moreChecklist: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorCompoundBrandForeground1,
    cursor: "pointer",
    paddingLeft: "16px",
    marginTop: "1px",
    ":hover": {
      textDecoration: "underline",
    },
  },
});

export interface TaskCardProps {
  task: ExecutionTask;
  ctx: TaskQueryContext;
  projectId: string;
  /** shown above the title on cross-project lists */
  projectName?: string;
  selected?: boolean;
  compact?: boolean;
  hideStatus?: boolean;
  hidePriority?: boolean;
  hideScope?: boolean;
  axis?: GroupKey;
  onOpen: (id: string) => void;
  /** when omitted the card is read-only (no checkbox / menu) */
  onStatus?: (id: string, status: ExecutionTask["status"]) => void;
  onPriority?: (id: string, priority: ExecutionPriority | undefined) => void;
  onAssignees?: (id: string, names: string[]) => void;
  /** 담당 메뉴에 올릴 이름 */
  assigneeOptions?: string[];
  onToggleChecklist?: (taskId: string, itemId: string, done: boolean) => void;
  onDelete?: (id: string) => void;
  onDuplicate?: (id: string) => void;
  className?: string;
}

export function TaskCard({
  task,
  ctx,
  projectId,
  projectName,
  selected,
  compact,
  hideStatus,
  hidePriority,
  hideScope,
  axis,
  onOpen,
  onStatus,
  onPriority,
  onAssignees,
  assigneeOptions,
  onToggleChecklist,
  onDelete,
  onDuplicate,
  className,
}: TaskCardProps) {
  const styles = useStyles();
  const labels = ctx.project.planner.labels;
  const done = task.status === "done";
  const bucket = ctx.project.planner.buckets.find((b) => b.id === task.bucketId);
  const cover = task.coverAttachmentId ? task.attachments.find((a) => a.id === task.coverAttachmentId) : undefined;

  const showStatus = !compact && !(hideStatus || axis === "status") && !!task.status;
  const showPriority = !(hidePriority || axis === "priority") && task.priority === "urgent";
  const hasDue = !!task.due;
  const hasLabels = !compact && task.labelIds.length > 0;
  const hasChips = showStatus || showPriority || hasDue || hasLabels;

  const showOnCard = !compact && task.showChecklistOnCard !== false && task.checklist.length > 0;
  const visibleChecklist = showOnCard ? task.checklist.slice(0, 3) : [];
  const remainingChecklist = showOnCard ? task.checklist.length - visibleChecklist.length : 0;

  return (
    <Card
      className={mergeClasses(styles.card, selected && styles.selected, done && styles.done, className)}
      size="small"
      appearance="filled"
      onClick={() => onOpen(task.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.currentTarget === e.target) onOpen(task.id);
      }}
      tabIndex={0}
      role="button"
      aria-label={task.title || "제목 없는 Task"}
      data-execution-id={task.id}
    >
      {cover?.url && (
        <div className={styles.coverWrap}>
          <img src={cover.url} alt={cover.title} className={styles.coverImg} />
        </div>
      )}
      {projectName && <Caption1 className={styles.project}>{projectName}</Caption1>}

      {/* Row 1: Title (1-line ellipsis + tooltip) + Actions */}
      <div className={styles.titleRow}>
        <div className={styles.titleWrap}>
          <Tooltip content={task.title || "제목 없음"} relationship="label">
            <Body1 className={mergeClasses(styles.title, done && styles.titleDone)}>
              {task.title || <span style={{ color: tokens.colorNeutralForeground4 }}>제목 없음</span>}
            </Body1>
          </Tooltip>
        </div>
        <TaskCommandMenu
            task={{ id: task.id, title: task.title, status: task.status, priority: task.priority, assignees: task.assignees }}
            canWrite={Boolean(onStatus || onPriority || onAssignees || onDuplicate || onDelete)}
            assignees={assigneeOptions}
            onOpen={() => onOpen(task.id)}
            onStatus={onStatus ? (status) => onStatus(task.id, status) : undefined}
            onPriority={onPriority ? (priority) => onPriority(task.id, priority) : undefined}
            onAssignees={onAssignees ? (names) => onAssignees(task.id, names) : undefined}
            onDuplicate={onDuplicate ? () => onDuplicate(task.id) : undefined}
            onDelete={onDelete ? () => onDelete(task.id) : undefined}
            trigger={
              <FuiButton
                className={styles.moreBtn}
                density="dialog"
                appearance="subtle"
                icon={<MoreHorizontal16Regular />}
                aria-label="더 보기"
                onClick={(e) => e.stopPropagation()}
              />
            }
          />
      </div>

      {/* Row 2: Description (100% width, 1-line ellipsis + tooltip) */}
      {task.notes && (
        <div className={styles.notesRow}>
          <Tooltip content={task.notes} relationship="description">
            <Caption1 className={mergeClasses(styles.notes, done && styles.notesDone)}>
              {task.notes}
            </Caption1>
          </Tooltip>
        </div>
      )}

      {/* Row 3: Essential chips only (Due, Urgent/Important Priority, Dot Labels, Status) */}
      {!compact && hasChips && (
        <div className={styles.chipsRow}>
          {showStatus && <StatusBadge status={task.status} size="small" />}
          {showPriority && <PriorityBadge priority={task.priority} size="small" />}
          {hasDue && <DueChip task={task} today={ctx.today} size="small" />}
          {hasLabels && <LabelChips labelIds={task.labelIds} labels={labels} size="small" variant="dot" max={2} nowrap />}
        </div>
      )}

      {/* 검수 칩: 누르면 카드를 열지 않고 미니 팝오버 */}
      {!compact && task.gateTags && task.gateTags.length > 0 && (
        <div className={styles.chipsRow}>
          {task.gateTags.map((g) => (
            <GateTagMicroPopover key={g.id} taskId={task.id} gate={g} />
          ))}
        </div>
      )}

      {/* Compact mode chips */}
      {compact && (
        <div className={styles.chipsRow}>
          {hasDue && <DueChip task={task} today={ctx.today} size="small" />}
          {showPriority && <PriorityBadge priority={task.priority} size="small" />}
          {bucket && <Caption1 className={styles.bucketText}>{bucket.name}</Caption1>}
        </div>
      )}

      {/* On-Card Checklist (Planner style) */}
      {showOnCard && (
        <div className={styles.onCardChecklist}>
          {visibleChecklist.map((c) => (
            <div
              key={c.id}
              className={styles.onCardCheckRow}
              onClick={(e) => e.stopPropagation()}
            >
              <Checkbox
                className={styles.onCardCheckbox}
                shape="circular"
                checked={c.done}
                aria-label={c.text}
                onClick={(e) => e.stopPropagation()}
                onChange={(_, d) => {
                  onToggleChecklist?.(task.id, c.id, !!d.checked);
                }}
              />
              <Caption1 className={mergeClasses(styles.onCardCheckText, c.done && styles.notesDone)}>
                {c.text}
              </Caption1>
            </div>
          ))}
          {remainingChecklist > 0 && (
            <Caption1
              className={styles.moreChecklist}
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                onOpen(task.id);
              }}
            >
              +{remainingChecklist}개 더보기
            </Caption1>
          )}
        </div>
      )}

      {/* Row 4: Footer (Left: Linked WBS Scope, Right: Checklist/Comments/Attachments/Assignees) */}
      <div className={styles.footer}>
        <div className={styles.footerLeft}>
          {!compact && !(hideScope || (axis && isScopeGroupKey(axis))) && (
            <ScopeChips task={task} ctx={ctx} onOpen={(id) => openScopeInSchedule(projectId, id)} max={1} nowrap />
          )}
        </div>
        <div className={styles.footerRight}>
          {!showOnCard && <ChecklistMini task={task} />}
          {task.comments.filter((c) => !c.deleted).length > 0 && (
            <span className={styles.metaItem} aria-label={`댓글 ${task.comments.filter((c) => !c.deleted).length}`}>
              <Comment12Regular />
              <Caption1>{task.comments.filter((c) => !c.deleted).length}</Caption1>
            </span>
          )}
          {task.attachments.length > 0 && (
            <span className={styles.metaItem} aria-label={`첨부 ${task.attachments.length}`}>
              <Attach12Regular />
              <Caption1>{task.attachments.length}</Caption1>
            </span>
          )}
          <AssigneeAvatars names={task.assignees} size={20} max={2} />
        </div>
      </div>
    </Card>
  );
}

/** Card bound to the open project's store actions. */
export function ProjectTaskCard(props: Omit<TaskCardProps, "onOpen" | "onStatus" | "onPriority" | "onAssignees" | "assigneeOptions" | "onDelete" | "onDuplicate" | "onToggleChecklist" | "projectId"> & { projectId?: string }) {
  const activeId = useProjectStore((s) => s.activeId) ?? "";
  const project = useProjectStore((s) => s.project);
  const names = knownAssignees(project);
  const surface = usePlanSurface();
  const own = useOwnWork();
  const canWrite = surface.editTask && own.executionOwn(props.task.assignees);
  const s = useProjectStore.getState;
  return (
    <TaskCard
      {...props}
      projectId={props.projectId ?? activeId}
      assigneeOptions={names}
      onOpen={(id) => s().setExecutionFocus(id)}
      onStatus={canWrite ? (id, status) => s().setExecutionStatus(id, status) : undefined}
      onPriority={canWrite ? (id, priority) => s().updateExecutionTasks([id], { priority }, `우선순위 → ${priority ? PRIORITY_LABEL[priority] : "미정"}`) : undefined}
      onAssignees={canWrite ? (id, assignees) => s().updateExecutionTasks([id], { assignees }, "담당 변경") : undefined}
      onToggleChecklist={(taskId, itemId, done) => s().updateChecklistItem(taskId, itemId, { done })}
      onDelete={canWrite ? (id) => s().deleteExecutionTasks([id]) : undefined}
      onDuplicate={canWrite ? (id) => s().duplicateExecutionTask(id) : undefined}
    />
  );
}
